import React, { useState } from 'react';
import styled, { createGlobalStyle, css, keyframes } from 'styled-components';
import { CONTENT } from './content';
import { useTokenomics } from './useTokenomics';
import {
  flattenMilestones,
  getFoldExpandLabel,
  getRoadmapEntries,
  getStatusPill,
  getTitleLines,
  isRecapGroup,
  RoadmapMilestone,
} from '../domain/roadmap/roadmapMilestones';

/**
 * Shared pieces of the drawn redesigns (D, E, F): the original site fonts, the brand
 * palette, hand-drawn ink strokes, and the content blocks every design shows in its own
 * way. Nothing here draws a card: the blocks are text, links and art that each design
 * places straight onto its illustration.
 */

export const C = {
  blue: '#3D8DF5',
  sky: '#5FA6F7',
  light: '#8CC4F8',
  mist: '#D6E9FF',
  paper: '#EEF6FF',
  yellow: '#F7E03C',
  orange: '#F6A21E',
  pink: '#F45DB3',
  green: '#3BB54A',
  ink: '#0E1B33',
  white: '#FFFFFF',
};

export const EASE_OUT = 'cubic-bezier(0.22, 1, 0.36, 1)';

export const hover = (rules: ReturnType<typeof css> | string) => css`
  @media (hover: hover) and (pointer: fine) {
    ${rules}
  }
`;

export const RedesignFonts = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Titan+One&family=Ubuntu:wght@400;500;700&family=Ubuntu+Mono:wght@700&display=swap');
  @font-face {
    font-family: 'GROBOLD';
    src: url('/assets/fonts/GROBOLD.woff2') format('woff2');
    font-display: swap;
  }
  @font-face {
    font-family: 'BreakingTheComic';
    src: url('/assets/fonts/BreakingTheComic.woff2') format('woff2');
    font-display: swap;
  }
  html, body {
    margin: 0;
    padding: 0;
    overflow-x: clip;
    -webkit-tap-highlight-color: transparent;
  }
  * { box-sizing: border-box; }
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
`;

/* ------------------------------------------------------------------ ink strokes */

/** A wobbly hand-drawn underline. It draws itself when its `.ink-host` is hovered. */
export const InkUnderline: React.FC<{ color?: string; width?: number }> = ({
  color = C.ink,
  width = 5,
}) => (
  <UnderlineSvg viewBox="0 0 300 16" preserveAspectRatio="none" aria-hidden="true">
    <path
      d="M3 10 C 40 4, 80 14, 120 8 S 200 4, 240 10 S 290 8, 297 6"
      fill="none"
      stroke={color}
      strokeWidth={width}
      strokeLinecap="round"
    />
  </UnderlineSvg>
);

const UnderlineSvg = styled.svg`
  position: absolute;
  left: -2%;
  bottom: -0.35em;
  width: 104%;
  height: 0.5em;
  overflow: visible;
  pointer-events: none;
  path {
    stroke-dasharray: 320;
    stroke-dashoffset: 320;
    transition: stroke-dashoffset 380ms ${EASE_OUT};
  }
  .ink-host:focus-visible & path,
  .ink-host[data-ink='on'] & path {
    stroke-dashoffset: 0;
  }
  ${hover(css`
    .ink-host:hover & path {
      stroke-dashoffset: 0;
    }
  `)}
`;

export const Sparkle: React.FC<{ size?: number; color?: string; style?: React.CSSProperties; className?: string }> = ({
  size = 28,
  color = C.white,
  style,
  className,
}) => (
  <svg width={size} height={size} viewBox="0 0 40 40" style={style} className={className} aria-hidden="true">
    <path
      d="M20 2 C 21 14, 26 19, 38 20 C 26 21, 21 26, 20 38 C 19 26, 14 21, 2 20 C 14 19, 19 14, 20 2 Z"
      fill={color}
      stroke={C.ink}
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
  </svg>
);

export const Bolt: React.FC<{ size?: number; style?: React.CSSProperties; className?: string }> = ({
  size = 60,
  style,
  className,
}) => (
  <svg width={size} height={size * 1.6} viewBox="0 0 50 80" style={style} className={className} aria-hidden="true">
    <path
      d="M30 2 L6 44 L22 44 L14 78 L44 30 L27 30 L36 2 Z"
      fill={C.yellow}
      stroke={C.ink}
      strokeWidth="3.5"
      strokeLinejoin="round"
    />
  </svg>
);

export const InkCheck: React.FC<{ size?: number }> = ({ size = 34 }) => (
  <CheckSvg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true">
    <path d="M6 22 C 10 25, 13 29, 16 33 C 21 22, 28 12, 36 5" fill="none" stroke={C.ink} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
  </CheckSvg>
);

const CheckSvg = styled.svg`
  flex: none;
  transition: transform 260ms ${EASE_OUT};
  ${hover(css`
    .ink-host:hover & {
      transform: rotate(-8deg) scale(1.12);
    }
  `)}
`;

/** A scribbled ring, drawn as if circled with a marker. */
export const InkRing: React.FC<{ color?: string; className?: string }> = ({ color = C.ink, className }) => (
  <svg viewBox="0 0 200 120" preserveAspectRatio="none" className={className} aria-hidden="true">
    <path
      d="M100 8 C 160 6, 196 30, 192 62 C 188 98, 140 114, 92 112 C 40 110, 6 88, 10 56 C 14 24, 60 6, 120 12"
      fill="none"
      stroke={color}
      strokeWidth="4"
      strokeLinecap="round"
    />
  </svg>
);

/* ------------------------------------------------------------- title artwork */

/** The original title lettering, trimmed of its transparent margins so headings sit tight. */
export const TITLES = {
  zeus: { src: '/redesign2/titles/zeus.webp', alt: "ZEUS Pepe's Dog" },
  pepesDog: { src: '/redesign2/titles/pepesDog.webp', alt: "Pepe's Dog" },
  takeOver: { src: '/redesign2/titles/takeOver.webp', alt: 'Community take over' },
  tokenomics: { src: '/redesign2/titles/tokenomics.webp', alt: "Tokenomics. Don't trust, verify!" },
  roadmap: { src: '/redesign2/titles/roadmap.webp', alt: 'Roadmap' },
  artist: { src: '/redesign2/titles/artist.webp', alt: 'Our artist' },
  memes: { src: '/redesign2/titles/memes.webp', alt: 'Memes' },
  comic: { src: '/redesign2/titles/comic.webp', alt: "Pepe's Dog comic" },
};

export const TitleArt = styled.img<{ $tilt?: number; $w?: string }>`
  display: block;
  width: ${(p) => p.$w || 'min(560px, 86vw)'};
  height: auto;
  transform: rotate(${(p) => p.$tilt ?? -2}deg);
  transition: transform 320ms ${EASE_OUT};
  user-select: none;
  ${hover(css`
    &:hover {
      transform: rotate(0deg) scale(1.03);
    }
  `)}
`;

/**
 * The lettering of the title artwork rebuilt in CSS, for the few headings that have no
 * image ("WHERE'S THE SUPPLY, ANON?"): yellow fill with halftone, black outline, white
 * outer outline and a black extrusion, in GROBOLD.
 */
export const Lettering = styled.span<{ $size?: string; $tilt?: number }>`
  display: inline-block;
  font-family: 'GROBOLD', 'Titan One', sans-serif;
  font-size: ${(p) => p.$size || 'clamp(28px, 4vw, 52px)'};
  line-height: 1.05;
  letter-spacing: 0.01em;
  transform: rotate(${(p) => p.$tilt ?? -2}deg);
  color: ${C.yellow};
  background-image: radial-gradient(circle at 50% 50%, ${C.orange} 28%, transparent 30%),
    linear-gradient(180deg, ${C.yellow} 30%, #ffc21a 100%);
  background-size: 7px 7px, 100% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  -webkit-text-stroke: 2px ${C.ink};
  paint-order: stroke fill;
  filter: drop-shadow(0 0 0 ${C.white}) drop-shadow(2px 0 0 ${C.white}) drop-shadow(-2px 0 0 ${C.white})
    drop-shadow(0 2px 0 ${C.white}) drop-shadow(0 -2px 0 ${C.white}) drop-shadow(4px 5px 0 ${C.ink});
`;

/* ------------------------------------------------------------- buy + contract */

export const BuyButton = styled.a<{ $big?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: ${(p) => (p.$big ? '18px 46px' : '13px 34px')};
  font-family: 'GROBOLD', sans-serif;
  font-size: ${(p) => (p.$big ? '30px' : '22px')};
  color: ${C.ink};
  text-decoration: none;
  background: ${C.yellow};
  border: 3.5px solid ${C.ink};
  border-radius: 999px 860px 920px 980px / 980px 900px 960px 880px;
  box-shadow: 4px 5px 0 ${C.ink};
  transition: transform 160ms ${EASE_OUT}, box-shadow 160ms ${EASE_OUT};
  user-select: none;
  &:active {
    transform: translate(3px, 4px) scale(0.98);
    box-shadow: 1px 1px 0 ${C.ink};
  }
  &:focus-visible {
    outline: 3px dashed ${C.ink};
    outline-offset: 5px;
  }
  ${hover(css`
    &:hover {
      transform: translate(-1px, -2px) rotate(-1.5deg);
      box-shadow: 6px 8px 0 ${C.ink};
    }
    &:hover:active {
      transform: translate(3px, 4px) scale(0.98);
      box-shadow: 1px 1px 0 ${C.ink};
    }
  `)}
`;

export const Buy: React.FC<{ big?: boolean }> = ({ big }) => (
  <BuyButton href={CONTENT.hero.buyLink} target="_blank" rel="noopener noreferrer" $big={big}>
    BUY ZEUS
  </BuyButton>
);

export const ContractAddress: React.FC<{ tone?: 'light' | 'dark'; className?: string }> = ({
  tone = 'dark',
  className,
}) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CONTENT.hero.ca);
    } catch {
      /* clipboard can be blocked; the address stays selectable */
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };
  return (
    <CaRow $tone={tone} className={className}>
      <span className="ca-label">CA</span>
      <span className="ca-value">{CONTENT.hero.ca}</span>
      <span className="ca-short" aria-hidden="true">
        {`${CONTENT.hero.ca.slice(0, 6)}…${CONTENT.hero.ca.slice(-4)}`}
      </span>
      <button type="button" onClick={copy} aria-label="Copy contract address" className="ink-host">
        {copied ? 'Copied!' : 'Copy'}
      </button>
      <span className="ca-toast" role="status" aria-live="polite">
        {copied ? 'Contract address copied' : ''}
      </span>
    </CaRow>
  );
};

const CaRow = styled.div<{ $tone: 'light' | 'dark' }>`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  max-width: 100%;
  padding: 4px 4px 4px 16px;
  color: ${C.ink};
  background: ${(p) => (p.$tone === 'dark' ? 'rgba(255, 255, 255, 0.92)' : C.white)};
  border: 3px solid ${C.ink};
  border-radius: 999px;
  box-shadow: 3px 4px 0 ${C.ink};
  .ca-short {
    display: none;
    font-family: 'Ubuntu Mono', ui-monospace, monospace;
    font-weight: 700;
    font-size: 16px;
  }
  @media (max-width: 600px) {
    .ca-value {
      display: none;
    }
    .ca-short {
      display: inline;
    }
  }
  .ca-label {
    font-family: 'GROBOLD', sans-serif;
    font-size: 18px;
  }
  .ca-value {
    font-family: 'Ubuntu Mono', ui-monospace, monospace;
    font-weight: 700;
    font-size: clamp(13px, 1.15vw, 16px);
    white-space: nowrap;
    user-select: all;
  }
  button {
    min-height: 44px;
    min-width: 72px;
    padding: 0 14px;
    font-family: 'GROBOLD', sans-serif;
    font-size: 16px;
    color: ${C.ink};
    background: ${C.white};
    border: 3px solid ${C.ink};
    border-radius: 999px;
    cursor: pointer;
    transition: transform 160ms ${EASE_OUT}, background-color 160ms ${EASE_OUT};
    &:active {
      transform: scale(0.95);
    }
    &:focus-visible {
      outline: 3px dashed currentColor;
      outline-offset: 3px;
    }
    ${hover(css`
      &:hover {
        background: ${C.yellow};
        transform: rotate(-3deg);
      }
    `)}
  }
  .ca-toast {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }
`;

/* ------------------------------------------------------------------- socials */

const XIcon = () => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d="M15.6993 0.953125H18.7565L12.0781 8.60553L19.9356 19.0204H13.784L8.96246 12.7051L3.4517 19.0204H0.391592L7.53422 10.8326L0 0.954549H6.30818L10.6598 6.7259L15.6993 0.953125ZM14.6242 17.1864H16.3187L5.3826 2.69179H3.56562L14.6242 17.1864Z" />
  </svg>
);
const TelegramIcon = () => (
  <svg viewBox="0 0 25 21" aria-hidden="true">
    <path d="M21.8964 0.189442C22.1963 0.0632066 22.5246 0.0196687 22.847 0.0633598C23.1695 0.107051 23.4744 0.236374 23.7299 0.437868C23.9854 0.639363 24.1823 0.905662 24.2999 1.20905C24.4176 1.51244 24.4518 1.84182 24.399 2.16292L21.6463 18.8598C21.3793 20.4703 19.6122 21.394 18.1351 20.5917C16.8996 19.9205 15.0645 18.8865 13.4138 17.8075C12.5885 17.2674 10.0604 15.5379 10.3711 14.3072C10.6381 13.2549 14.886 9.30068 17.3134 6.94974C18.2662 6.02612 17.8317 5.49331 16.7066 6.34289C13.9127 8.4523 9.42683 11.6601 7.94369 12.5631C6.63532 13.3593 5.95322 13.4952 5.13762 13.3593C3.64962 13.1117 2.26965 12.7282 1.14334 12.2609C-0.378642 11.6298 -0.304606 9.53735 1.14212 8.92807L21.8964 0.189442Z" />
  </svg>
);
const CmcIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20.5899 14.3418C20.1713 14.6061 19.6785 14.6391 19.3038 14.4277C18.828 14.1591 18.5661 13.5309 18.5661 12.6544V10.0363C18.5661 8.77185 18.0662 7.87225 17.2303 7.62918C15.8144 7.21666 14.7486 8.95074 14.3479 9.60066L11.8497 13.6512V8.70058C11.8214 7.56121 11.4518 6.87966 10.749 6.67435C10.2846 6.53842 9.58889 6.59317 8.91348 7.62682L3.31621 16.6139C2.56687 15.1914 2.17624 13.6075 2.17825 11.9998C2.17825 6.58562 6.51674 2.18246 11.8497 2.18246C17.1827 2.18246 21.5207 6.58703 21.5207 11.9998C21.5207 12.0092 21.5231 12.0172 21.5235 12.0257C21.524 12.0342 21.5235 12.0437 21.5235 12.0531C21.574 13.1014 21.2347 13.9354 20.5914 14.3418H20.5899ZM23.6696 12.0007V11.9738V11.9469C23.639 5.35279 18.3489 0 11.8492 0C5.33206 0 0.0292969 5.383 0.0292969 12.0002C0.0292969 18.6175 5.33206 24 11.8497 24C14.8391 24.0005 17.715 22.8553 19.8857 20.7999C20.0955 20.6015 20.219 20.3287 20.2296 20.0401C20.2403 19.7516 20.1373 19.4704 19.9428 19.257C19.7533 19.0482 19.4885 18.9231 19.2068 18.9094C18.9251 18.8957 18.6495 18.9944 18.4405 19.1839L18.4245 19.1985C16.6486 20.8804 14.2956 21.8176 11.8497 21.8171C8.99419 21.8171 6.42518 20.5531 4.65334 18.549L9.70076 10.4455V14.1818C9.70076 15.9753 10.3969 16.5568 10.9803 16.7262C11.5637 16.8957 12.4572 16.78 13.3945 15.2584L16.1712 10.7575C16.2604 10.6121 16.3421 10.4871 16.4171 10.3799V12.6558C16.4171 14.3337 17.0892 15.6765 18.2607 16.3373C19.3165 16.933 20.6442 16.8792 21.726 16.1957C23.0367 15.366 23.7433 13.84 23.6696 12.0007Z" />
  </svg>
);
const YoutubeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8ZM9.6 15.6V8.4l6.2 3.6-6.2 3.6Z" />
  </svg>
);

export const SOCIALS = [
  { href: CONTENT.social.x, label: 'X', Icon: XIcon },
  { href: CONTENT.social.telegram, label: 'Telegram', Icon: TelegramIcon },
  { href: CONTENT.social.coinmarketcap, label: 'CoinMarketCap', Icon: CmcIcon },
  { href: CONTENT.social.youtube, label: 'YouTube', Icon: YoutubeIcon },
];

export const Socials: React.FC<{ color?: string; size?: number; className?: string }> = ({
  color = C.ink,
  size = 26,
  className,
}) => (
  <SocialRow className={className} $color={color} $size={size}>
    {SOCIALS.map(({ href, label, Icon }) => (
      <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
        <Icon />
      </a>
    ))}
  </SocialRow>
);

const wiggle = keyframes`
  0% { transform: rotate(0deg) scale(1.1); }
  30% { transform: rotate(-10deg) scale(1.15); }
  60% { transform: rotate(8deg) scale(1.15); }
  100% { transform: rotate(0deg) scale(1.12); }
`;

const SocialRow = styled.div<{ $color: string; $size: number }>`
  display: flex;
  gap: 6px;
  a {
    display: grid;
    place-items: center;
    width: 48px;
    height: 48px;
    color: ${(p) => p.$color};
    border-radius: 50%;
    &:focus-visible {
      outline: 3px dashed currentColor;
      outline-offset: 2px;
    }
  }
  svg {
    width: ${(p) => p.$size}px;
    height: ${(p) => p.$size}px;
    fill: currentColor;
  }
  ${hover(css`
    a:hover svg {
      animation: ${wiggle} 360ms ${EASE_OUT} forwards;
    }
  `)}
`;

/* ----------------------------------------------------------------------- nav */

export const NAV = [
  { label: 'HOME', href: '#top' },
  { label: 'REFERRAL', href: CONTENT.menu.referral, external: true },
  { label: 'TOKENOMICS', href: '#tokenomics' },
  { label: 'ROADMAP', href: '#roadmap' },
];

export const UTILITIES = [
  { label: 'Get ENS', href: '/ens' },
  { label: 'Governance', href: '/governance' },
  { label: 'Earn', href: CONTENT.menu.earn, external: true },
  { label: 'Bridge', href: CONTENT.menu.bridge, external: true },
];

export type NavItem = { label: string; href: string; external?: boolean };

export const DrawnNav: React.FC<{ color?: string; accent?: string; items?: NavItem[]; home?: string }> = ({
  color = C.white,
  accent = C.yellow,
  items = NAV,
  home = '#top',
}) => {
  const [open, setOpen] = useState(false);
  return (
    <NavBar $color={color} $accent={accent}>
      <a href={home} className="logo" aria-label="Pepe's Dog home">
        <img src="/assets/img/logo.webp" alt="" />
      </a>
      <button type="button" className="burger" aria-expanded={open} aria-controls="drawn-menu" onClick={() => setOpen(!open)}>
        {open ? 'CLOSE' : 'MENU'}
      </button>
      <ul id="drawn-menu" className={open ? 'open' : ''}>
        {items.map((item) => (
          <li key={item.label}>
            <a
              className="ink-host"
              href={item.href}
              onClick={() => setOpen(false)}
              {...(item.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
              {item.label}
              <InkUnderline color={accent} />
            </a>
          </li>
        ))}
        <li className="utilities">
          <button type="button" className="ink-host" aria-haspopup="true">
            UTILITIES
            <InkUnderline color={accent} />
          </button>
          <ul>
            {UTILITIES.map((u) => (
              <li key={u.label}>
                <a href={u.href} {...(u.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
                  {u.label}
                </a>
              </li>
            ))}
          </ul>
        </li>
        <li className="socials">
          <Socials color={color} size={20} />
        </li>
      </ul>
    </NavBar>
  );
};

const NavBar = styled.nav<{ $color: string; $accent: string }>`
  position: relative;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  max-width: 1320px;
  margin: 0 auto;
  padding: 18px clamp(16px, 4vw, 40px);
  font-family: 'BreakingTheComic', 'GROBOLD', sans-serif;
  .logo img {
    display: block;
    width: 58px;
    height: 58px;
    transition: transform 260ms ${EASE_OUT};
  }
  ${hover(css`
    .logo:hover img {
      transform: rotate(-8deg) scale(1.06);
    }
  `)}
  > ul {
    display: flex;
    align-items: center;
    gap: clamp(14px, 2.2vw, 34px);
    margin: 0;
    padding: 0;
    list-style: none;
  }
  > ul > li > a,
  > ul > li > button {
    position: relative;
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    padding: 0;
    font: inherit;
    font-size: 19px;
    letter-spacing: 0.02em;
    color: ${(p) => p.$color};
    background: none;
    border: 0;
    text-decoration: none;
    cursor: pointer;
  }
  .utilities {
    position: relative;
  }
  .utilities ul {
    position: absolute;
    top: 100%;
    left: -14px;
    min-width: 170px;
    margin: 0;
    padding: 10px 0;
    list-style: none;
    background: ${C.white};
    border: 3px solid ${C.ink};
    border-radius: 14px 18px 12px 20px;
    box-shadow: 4px 5px 0 ${C.ink};
    opacity: 0;
    visibility: hidden;
    transform: translateY(-6px) rotate(-1deg);
    transform-origin: top left;
    transition: opacity 180ms ${EASE_OUT}, transform 180ms ${EASE_OUT}, visibility 180ms;
  }
  .utilities:focus-within ul {
    opacity: 1;
    visibility: visible;
    transform: none;
  }
  ${hover(css`
    .utilities:hover ul {
      opacity: 1;
      visibility: visible;
      transform: none;
    }
  `)}
  .utilities ul a {
    display: block;
    padding: 10px 18px;
    font-size: 18px;
    color: ${C.ink};
    text-decoration: none;
  }
  ${hover(css`
    .utilities ul a:hover {
      background: ${C.yellow};
    }
  `)}
  .burger {
    display: none;
  }
  @media (max-width: 900px) {
    .burger {
      display: block;
      min-height: 44px;
      padding: 0 18px;
      font-family: 'GROBOLD', sans-serif;
      font-size: 16px;
      color: ${C.ink};
      background: ${(p) => p.$accent};
      border: 3px solid ${C.ink};
      border-radius: 999px;
      box-shadow: 3px 3px 0 ${C.ink};
    }
    > ul {
      position: absolute;
      top: calc(100% - 4px);
      right: 16px;
      left: 16px;
      flex-direction: column;
      align-items: flex-start;
      gap: 4px;
      padding: 18px 22px;
      background: ${C.white};
      border: 3px solid ${C.ink};
      border-radius: 18px 24px 16px 22px;
      box-shadow: 5px 6px 0 ${C.ink};
      opacity: 0;
      visibility: hidden;
      transform: translateY(-8px);
      transition: opacity 200ms ${EASE_OUT}, transform 200ms ${EASE_OUT}, visibility 200ms;
    }
    > ul.open {
      opacity: 1;
      visibility: visible;
      transform: none;
    }
    > ul > li > a,
    > ul > li > button {
      color: ${C.ink};
      font-size: 22px;
    }
    .utilities ul {
      position: static;
      opacity: 1;
      visibility: visible;
      transform: none;
      border: 0;
      box-shadow: none;
      padding: 0 0 6px 12px;
    }
    .socials a {
      color: ${C.ink};
    }
  }
`;

/* -------------------------------------------------------------------- footer */

export const DrawnFooter: React.FC<{ color?: string }> = ({ color = C.ink }) => (
  <FooterRow $color={color}>
    <svg className="line" viewBox="0 0 1200 20" preserveAspectRatio="none" aria-hidden="true">
      <path d="M2 12 C 200 2, 400 18, 600 10 S 1000 4, 1198 12" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" />
    </svg>
    <div className="inner">
      <img src="/assets/img/logo.webp" alt="" />
      <nav aria-label="Legal">
        <a href="/terms">Terms &amp; Conditions</a>
        <a href="/privacy">Privacy Policy</a>
      </nav>
      <Socials color={color} size={20} />
    </div>
  </FooterRow>
);

const FooterRow = styled.footer<{ $color: string }>`
  max-width: 1320px;
  margin: 0 auto;
  padding: 0 clamp(16px, 4vw, 40px) 40px;
  color: ${(p) => p.$color};
  .line {
    display: block;
    width: 100%;
    height: 14px;
  }
  .inner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding-top: 20px;
  }
  img {
    width: 48px;
    height: 48px;
  }
  nav {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 26px;
    font-family: 'Ubuntu', sans-serif;
    font-weight: 500;
  }
  nav a {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    color: inherit;
  }
`;

/* ------------------------------------------------------------- tokenomics */

export const TOKEN_FACTS = CONTENT.tokenomics.cards.map((card) => ({
  ...card,
  title: card.title === 'CONTRACT' ? 'cONTRACT' : card.title,
}));

const LP_PAIR_URL = 'https://etherscan.io/address/0xf97503af8230a7e72909d6614f45e88168ff3c10';

function compact(numStr: string): string {
  const n = Number(numStr);
  if (n >= 1e12) return `${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  return n.toLocaleString('en-US');
}

function usd(v: number): string {
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}

/**
 * Where the supply sits, drawn as an ink donut straight on the page. Same copy and
 * data as the live chart; while the API is down it shows the same honest grey ring and
 * message, never invented numbers.
 */
export const SupplyDonut: React.FC<{ ink?: string; size?: number; circColor?: string }> = ({
  ink = C.ink,
  size = 300,
  circColor = C.blue,
}) => {
  const { data, failed } = useTokenomics();
  const lp = data ? data.lpZeusPct : 0;
  const r = 70;
  const circ = 2 * Math.PI * r;
  return (
    <Donut $ink={ink}>
      <Lettering $size="clamp(24px, 2.6vw, 34px)" $tilt={-1.5}>
        WHERE'S THE SUPPLY, ANON?
      </Lettering>
      <p className="status">
        {data
          ? 'Live onchain data'
          : failed
          ? 'Onchain data unavailable right now, try again in a minute'
          : 'reading the chain...'}
      </p>
      <div className="body">
        <svg viewBox="0 0 200 200" width={size} height={size} role="img" aria-label={data ? `LP locked ${lp.toFixed(2)} percent, circulating ${data.circulatingPct.toFixed(2)} percent` : 'Supply chart loading'}>
          <circle cx="100" cy="100" r={r} fill="none" stroke={data ? circColor : '#d9d9d9'} strokeWidth="38" />
          {data && (
            <circle
              cx="100"
              cy="100"
              r={r}
              fill="none"
              stroke={C.yellow}
              strokeWidth="38"
              strokeDasharray={`${(lp / 100) * circ} ${circ}`}
              transform="rotate(-90 100 100)"
            />
          )}
          <circle cx="100" cy="100" r={r + 19} fill="none" stroke={ink} strokeWidth="3" />
          <circle cx="100" cy="100" r={r - 19} fill="none" stroke={ink} strokeWidth="3" />
        </svg>
        <ul>
          <li>
            <span className="swatch" style={{ background: C.yellow }} />
            <span>
              <strong>LP LOCKED FOREVER</strong>
              {data && (
                <>
                  <em>{data.lpUsd != null ? usd(data.lpUsd) : ''}</em>
                  <em>{`${lp.toFixed(2)}% · ${compact(data.lpZeus)} ZEUS`}</em>
                </>
              )}
              <small>Uniswap V2 pool, LP tokens burned to 0xdead</small>
            </span>
          </li>
          <li>
            <span className="swatch" style={{ background: circColor }} />
            <span>
              <strong>Circulating</strong>
              {data && <em>{`${data.circulatingPct.toFixed(2)}% · ${compact(data.circulatingZeus)} ZEUS`}</em>}
            </span>
          </li>
        </ul>
      </div>
      <p className="foot">
        Every trade on Uniswap V2 pays a 0.3% fee that stays inside the pool, and the LP tokens were burned, so
        this liquidity can never be pulled.{' '}
        <a href={LP_PAIR_URL} target="_blank" rel="noopener noreferrer">
          See the pool ↗
        </a>
      </p>
    </Donut>
  );
};

const Donut = styled.div<{ $ink: string }>`
  color: ${(p) => p.$ink};
  font-family: 'Ubuntu', sans-serif;
  .status {
    margin: 14px 0 0;
    font-size: 16px;
    font-weight: 500;
  }
  .body {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px 32px;
  }
  svg {
    max-width: 100%;
    height: auto;
    transition: transform 400ms ${EASE_OUT};
  }
  ${hover(css`
    .body:hover svg {
      transform: rotate(-6deg) scale(1.03);
    }
  `)}
  ul {
    display: grid;
    gap: 18px;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  li {
    display: flex;
    gap: 12px;
  }
  .swatch {
    flex: none;
    width: 22px;
    height: 22px;
    margin-top: 2px;
    border: 3px solid currentColor;
    border-radius: 6px 8px 5px 9px;
  }
  li > span:last-child {
    display: grid;
    gap: 2px;
  }
  strong {
    font-family: 'GROBOLD', sans-serif;
    font-weight: 400;
    font-size: 22px;
  }
  em {
    font-style: normal;
    font-weight: 700;
    font-size: 19px;
  }
  small {
    font-size: 15px;
  }
  .foot {
    max-width: 62ch;
    margin: 18px 0 0;
    font-size: 16px;
    line-height: 1.55;
  }
  .foot a {
    color: inherit;
    font-weight: 700;
  }
`;

/* ------------------------------------------------------------------ roadmap */

export interface RoadmapStop {
  milestone: RoadmapMilestone;
  lines: string[];
  pill: string | null;
}

const toStop = (milestone: RoadmapMilestone): RoadmapStop => ({
  milestone,
  lines: getTitleLines(milestone.title),
  pill: getStatusPill(milestone),
});

export const ROADMAP: RoadmapStop[] = flattenMilestones().map(toStop);

/**
 * The roadmap as the live page shows it: the 2025 recap stays folded behind one control
 * so the road reads past, now, next. Folded milestones stay in the document.
 */
export type RoadmapBlock =
  | { kind: 'stop'; stop: RoadmapStop }
  | { kind: 'fold'; id: string; title: string; payload: string; expand: string; stops: RoadmapStop[] };

export const ROADMAP_BLOCKS: RoadmapBlock[] = getRoadmapEntries().map((entry) =>
  isRecapGroup(entry)
    ? {
        kind: 'fold',
        id: entry.id,
        title: entry.title,
        payload: entry.payload,
        expand: getFoldExpandLabel(entry),
        stops: entry.milestones.map(toStop),
      }
    : { kind: 'stop', stop: toStop(entry) }
);

export const Fold = styled.details`
  summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 14px;
    min-height: 44px;
    cursor: pointer;
    list-style: none;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  .fold-title {
    font-family: 'GROBOLD', sans-serif;
    font-size: clamp(21px, 2vw, 28px);
  }
  .fold-payload {
    flex-basis: 100%;
    font-size: 16.5px;
  }
  .fold-toggle {
    display: inline-flex;
    align-items: center;
    min-height: 40px;
    padding: 0 16px;
    font-family: 'GROBOLD', sans-serif;
    font-size: 15px;
    color: ${C.ink};
    background: ${C.yellow};
    border: 3px solid ${C.ink};
    border-radius: 999px;
    box-shadow: 3px 3px 0 ${C.ink};
    transition: transform 160ms ${EASE_OUT};
  }
  summary:active .fold-toggle {
    transform: translate(2px, 2px);
  }
  summary:focus-visible {
    outline: 3px dashed ${C.ink};
    outline-offset: 4px;
  }
  &[open] .fold-toggle::after {
    content: ' ▲';
  }
  &:not([open]) .fold-toggle::after {
    content: ' ▼';
  }
`;

export const FoldSummary: React.FC<{ block: Extract<RoadmapBlock, { kind: 'fold' }> }> = ({ block }) => (
  <summary>
    <span className="fold-title">{block.title}</span>
    <span className="fold-toggle">{block.expand}</span>
    <span className="fold-payload">{block.payload}</span>
  </summary>
);

/* -------------------------------------------------------------------- memes */

export const MEMES = [
  { src: '/redesign2/memes/01.webp', name: 'zeus-top-gun', alt: 'Zeus in a bomber jacket and aviators giving a thumbs up on an aircraft carrier while jets draw green candles in the sky, Top Gun style', share: "I feel the need. The need for green candles. Zeus is flying top cover for your bags. $ZEUS #CC8" },
  { src: '/redesign2/memes/02.webp', name: 'zeus-ghostbusters', alt: 'Zeus blasting red candle ghosts with a proton pack while Pepe and Brett hide behind him, Ghostbusters style', share: "Who you gonna call when red candles haunt your portfolio? Zeus brought the proton pack. $ZEUS #CC8" },
  { src: '/redesign2/memes/03.webp', name: 'zeus-side-eye', alt: 'Zeus side-eyeing the camera next to a chewed phone full of red candles while Pepe holds up the empty case, the Monkey Puppet meme', share: "Pepe: who chewed my phone? Zeus, next to the red chart he ate: no idea. $ZEUS #CC8" },
  { src: '/redesign2/memes/04.webp', name: 'zeus-this-is-fine', alt: 'Zeus calmly sitting at his desk with a coffee while the trading office burns and every screen shows red candles, the This Is Fine meme', share: "Office on fire, every screen red, coffee still warm. Zeus says this is fine. Zeus holds. $ZEUS #CC8" },
  { src: '/redesign2/memes/05.webp', name: 'zeus-ferris-bueller', alt: 'Zeus singing on a giant green candle parade float while Andy dances in the crowd, Ferris Bueller style', share: "Life moves pretty fast. If you don't stop and buy the dip once in a while, you could miss it. $ZEUS #CC8" },
  { src: '/redesign2/memes/06.webp', name: 'zeus-yelling-at-cat', alt: 'Zeus sitting unbothered at the table with a green candle on his plate while Pepe cries and yells at him, the Woman Yelling at a Cat meme', share: "Pepe yelling about the market. Zeus calmly eating his green candle dinner. Be like Zeus. $ZEUS #CC8" },
  { src: '/redesign2/memes/07.webp', name: 'zeus-always-has-been', alt: 'Zeus floating behind Pepe in space, both in space suits, while Pepe stares at a planet made of green candles, the Always Has Been meme', share: "Wait, the whole planet is green candles? Always has been. $ZEUS #CC8" },
  { src: '/redesign2/memes/08.webp', name: 'zeus-goonies', alt: 'Zeus on a pile of gold coins on a pirate ship while Pepe, Brett and Andy climb up with flashlights, The Goonies style', share: "Goonies never say die. Zeus found the treasure first and he is not sharing. $ZEUS #CC8" },
  { src: '/redesign2/memes/09.webp', name: 'zeus-dirty-dancing', alt: 'Zeus lifted high over the head of Pepe in the lake at sunset, Dirty Dancing style', share: "Nobody puts Zeus in a corner. Pepe lifted him straight to the top of the chart. $ZEUS #CC8" },
  { src: '/redesign2/memes/10.webp', name: 'zeus-leo-cheers', alt: 'Zeus in a tuxedo raising a champagne glass while green candle fireworks burst behind him, the Leo cheers meme', share: "Raise a glass to everyone who bought before the fireworks. Cheers, anon. $ZEUS #CC8" },
  { src: '/redesign2/memes/11.webp', name: 'zeus-trading-places', alt: 'Zeus on a crate in the trading pit shouting buy under a board of green candles while Pepe holds his head, Trading Places style', share: "Buy! Buy! Buy! Zeus is in the pit while Pepe is still reading the whitepaper. $ZEUS #CC8" },
  { src: '/redesign2/memes/12.webp', name: 'zeus-scarface', alt: 'Zeus in a white suit buried in gold coins behind his desk in a Miami mansion, Scarface style', share: "Say hello to my little dog. $ZEUS #CC8" },
  { src: '/redesign2/memes/13.webp', name: 'zeus-risky-business', alt: 'Zeus sliding across the floor in socks and a pink shirt while the TV shows a green chart, Risky Business style', share: "Parents out of town, chart going up, socks on. Sometimes you gotta say what the heck and buy. $ZEUS #CC8" },
  { src: '/redesign2/memes/14.webp', name: 'zeus-chest-thump', alt: 'Zeus thumping his chest at a fancy lunch while Pepe stares, the Wolf of Wall Street chest thump', share: "Lunch lesson from Zeus: hum, thump your chest, never sell the dog. $ZEUS #CC8" },
  { src: '/redesign2/memes/15.webp', name: 'zeus-yacht', alt: 'Zeus throwing green banknotes off a superyacht, Wolf of Wall Street style', share: "I'm not leaving. The dog is not leaving. Nobody is selling. $ZEUS #CC8" },
  { src: '/redesign2/memes/16.webp', name: 'zeus-leo-pointing', alt: 'Zeus pointing at a giant green candle on the TV while Pepe sits next to him with a beer, the Leonardo DiCaprio pointing meme', share: "Pepe: that candle on TV looks familiar. Zeus, pointing: anon, that's us. $ZEUS #CC8" },
  { src: '/redesign2/memes/17.webp', name: 'zeus-rocky', alt: 'Zeus in a grey sweatsuit and beanie raising both fists at the top of the museum steps at sunrise under a green candle chart while Pepe climbs behind him out of breath, Rocky style', share: "Pepe is still on step 12. Zeus is already at the top, gonna fly now. $ZEUS #CC8" },
  { src: '/redesign2/memes/18.webp', name: 'zeus-pepe-silvia', alt: 'Zeus ranting in front of a wall of charts and whale photos tied with red string, the Pepe Silvia meme', share: "I connected every whale wallet, every chart, every candle. It all points to one dog. $ZEUS #CC8" },
];

/** The first twelve, for designs that show a shorter gallery. */
export const MEMES_12 = MEMES.slice(0, 12);

/* ------------------------------------------------------------- media + copy */

export const ORIGINAL_ART = '/assets/img/about/01.webp';
export const NDHAUS_PHOTO = '/assets/img/artist/01.webp';
export const COMIC_PAGES = ['/images/comic-pages/page-01.jpg', '/images/comic-pages/page-02.jpg', '/images/comic-pages/page-03.jpg'];

/** The whole comic: cover, 19 double-page spreads and the back cover. */
export const COMIC_BOOK = Array.from({ length: 21 }, (_, i) => ({
  src: `/images/comic-pages/page-${String(i + 1).padStart(2, '0')}.jpg`,
  spread: i > 0 && i < 20,
}));

/** Poster frames cut from each video itself, so a video never shows as a black box. */
export const VIDEOS = {
  trailer: { src: '/files/video/03.mp4', poster: '/redesign2/posters/03.webp' },
  buy: { src: '/files/video/01.mp4', poster: '/redesign2/posters/01.webp' },
};

export const Video: React.FC<{ video: { src: string; poster: string }; className?: string }> = ({ video, className }) => (
  <video className={className} src={video.src} poster={video.poster} controls playsInline autoPlay muted loop preload="metadata" />
);

/** The about paragraphs, with the trademark link exactly where the live page has it. */
export const AboutCopy: React.FC = () => {
  const [p1, p2, p3, p4] = CONTENT.about.paragraphs;
  const [before, after] = p4.split('has officially secured trademark');
  return (
    <>
      <p>{p1}</p>
      <p>{p2}</p>
      <p>{p3}</p>
      <p>
        {before}
        <a href={CONTENT.about.trademarkLink} target="_blank" rel="noopener noreferrer">
          has officially secured trademark
        </a>
        {after}
      </p>
    </>
  );
};

export const CtoCopy: React.FC = () => (
  <>
    {CONTENT.aboutCto.paragraphs.map((p) => (
      <p key={p.slice(0, 20)}>{p}</p>
    ))}
  </>
);

/** The artist paragraphs, with ndhaus and Jagged linked as on the live page. */
export const ArtistCopy: React.FC = () => {
  const [p1, p2, p3, p4, p5] = CONTENT.artist.paragraphs;
  const link = (href: string, label: string) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {label}
    </a>
  );
  const afterNd = p1.replace(/^On February 7, 2022, ndhaus /, '');
  const [preJagged, postJagged] = afterNd.split('Jon "Jagged" Eyrick');
  return (
    <>
      <p>
        On February 7, 2022, {link(CONTENT.artist.links.ndhaus, 'ndhaus')} {preJagged}Jon "
        {link(CONTENT.artist.links.jagged, 'Jagged')}" Eyrick{postJagged}
      </p>
      <p>{p2}</p>
      <p>{p3}</p>
      <p>{p4}</p>
      <p>{p5}</p>
    </>
  );
};
